import React, { Component, Fragment } from 'react';

import { InjectedIntl, injectIntl } from 'react-intl';

import { gridSize } from '@atlaskit/theme';

import KeyboardShortcuts from 'src/components/keyboard-shortcut-handler';
import SearchField from 'src/components/search-field';
import commonMessages from 'src/i18n/common';

import { FilterMode } from '../constants';

import messages from './file-filter/i18n';
import { FilterModeSelector } from './filter-mode-selector';
import indexI18n from './index.i18n';

export type Props = {
  intl: InjectedIntl;
  onClearFilter: () => void;
  onFilterChange: (query: string) => void;
  onCodeSearchChange: (query: string) => void;
  /* eslint react/no-unused-prop-types: "warn" */
  isFiltering: boolean;
  filterMode: FilterMode;
  onFilterModeChange: (filterMode: string) => void;
  isMainBranch: boolean;
};

type State = { value: string };

export default injectIntl(
  class extends Component<Props, State> {
    input: React.InputHTMLAttributes<HTMLInputElement> | null | undefined;

    state = { value: '' };

    componentDidMount() {
      if (
        !this.props.isMainBranch &&
        this.props.filterMode === FilterMode.Code
      ) {
        this.props.onFilterModeChange(FilterMode.Files);
      }
    }

    clearFilter = () => {
      this.props.onClearFilter();
      this.setState({ value: '' });
      this.unfocusQuery();
    };

    focusQuery = () => {
      if (this.input) {
        // @ts-ignore
        this.input.focus();
      }
    };

    unfocusQuery = () => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    selectAndFocusFilesFilterKeyboardShortcut = {
      id: 'select-focus-source-files-filter',
      description: this.props.intl.formatMessage(
        messages.keyboardShortcutFilterFiles
      ),
      // @ts-ignore TODO: fix noImplicitAny error here
      handler: e => {
        // Don't type an 'f' into the input
        e.preventDefault();
        if (this.props.filterMode === FilterMode.Files) {
          this.focusQuery();
        } else {
          this.props.onFilterModeChange(FilterMode.Files);
          this.focusQuery();
        }
      },
      keys: 'f',
    };

    selectAndFocusCodeFilterKeyboardShortcut = {
      id: 'select-focus-source-code-filter',
      description: this.props.intl.formatMessage(
        messages.keyboardShortcutCodeSearch
      ),
      // @ts-ignore TODO: fix noImplicitAny error here
      handler: e => {
        e.preventDefault();
        if (this.props.filterMode === FilterMode.Code) {
          this.focusQuery();
        } else if (
          this.props.filterMode === FilterMode.Files &&
          this.props.isMainBranch
        ) {
          this.props.onFilterModeChange(FilterMode.Code);
          this.focusQuery();
        }
      },
      keys: 'c',
    };
    // we only want to bind ESC when focus is in the input, so for now I think
    // we need 2 KeyboardShortcuts components :\
    clearFilterKeyboardShortcut = {
      id: 'clear-source-filter',
      description: this.props.intl.formatMessage(commonMessages.clearFilter),
      enableInFormFields: true,
      handler: this.clearFilter,
      keys: 'esc',
    };

    handleChange = (value: string) => {
      if (this.props.filterMode === FilterMode.Code) {
        this.props.onCodeSearchChange(value);
      } else {
        this.props.onFilterChange(value);
      }
    };

    render() {
      return (
        <Fragment>
          <KeyboardShortcuts
            groupId="source"
            groupTitle={this.props.intl.formatMessage(
              indexI18n.keyboardShortcutGroup
            )}
            shortcuts={[
              this.selectAndFocusCodeFilterKeyboardShortcut,
              this.selectAndFocusFilesFilterKeyboardShortcut,
            ]}
          >
            <KeyboardShortcuts
              groupId="source-esc"
              shortcuts={[this.clearFilterKeyboardShortcut]}
              requiresFocus
            >
              <SearchField
                fieldName="filter-files-input"
                currentSearch={this.state.value}
                handleFilterChange={(
                  e: React.SyntheticEvent<HTMLInputElement>
                ) => {
                  const { value } = e.currentTarget;
                  this.setState({ value });
                  this.handleChange(value);
                }}
                placeholder={this.props.intl.formatMessage(
                  this.props.filterMode === FilterMode.Code
                    ? messages.queryPlaceholderCode
                    : messages.queryPlaceholderFiles
                )}
                forwardedRef={(
                  ref:
                    | React.InputHTMLAttributes<HTMLInputElement>
                    | null
                    | undefined
                ) => {
                  this.input = ref;
                }}
                clearFilter={this.clearFilter}
                elemBeforeInput={
                  <FilterModeSelector
                    // Trigger the filtering/search when the filter mode is changed.
                    onChange={() => this.handleChange(this.state.value)}
                    isMainBranch={this.props.isMainBranch}
                  />
                }
                width={`${gridSize() * 35}px`}
              />
            </KeyboardShortcuts>
          </KeyboardShortcuts>
        </Fragment>
      );
    }
  }
);
